import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useRegionalLink } from 'hooks/useRegionalLink';

import { Text, Location } from 'components/common/Elements/Elements';

import { sizeToCompany } from 'utils/common';

import { useRightSideBar, Button, showNotif } from '@p81-common/ui';
import { links } from 'data/constants';
import classes from './CompanyList.module.scss';
import TenantDetails from '../../../TenantDetails/TenantDetails';
import { getAssociationFlow } from '../../../AssociateTenants/helpers/getAssociationFlow';
import { renderAssociationDrawer } from '../../../AssociateTenants/helpers/renderAssociationDrawer';
import { access, hasAccess, resources } from 'utils/permissions';

const CompanyList = ({
  info,
  companyName,
  tenantId,
  customerId,
  requestHeaderData,
  tenantUUID
}) => {
  const { t } = useTranslation(['activity']);
  const { setOpenSecondary, setComponentSecondaryDrawer } = useRightSideBar();
  const generateRegionalPath = useRegionalLink();

  const hasAssociationAccess = hasAccess([
    resources.CUSTOMERS_ASSOCIATION,
    access.WRITE
  ]);

  const onClickTenantDetailsButton = () => {
    setOpenSecondary(true);

    setComponentSecondaryDrawer(
      <TenantDetails company={{ companyName, tenantUUID, tenantId }} />
    );
  };

  const onClickAssociateButton = () => {
    const flow = getAssociationFlow(info.type);
    if (flow === 'unknown') {
      showNotif(t('tenantsAssociation.errors.UNKNOWN_TYPE', { companyName }), {
        alertProps: { severity: 'error' }
      });
      return;
    }
    setOpenSecondary(true);
    setComponentSecondaryDrawer(
      renderAssociationDrawer(flow, {
        companyName,
        tenantId,
        customerId,
        updateDataFn: requestHeaderData
      })
    );
  };

  return (
    <div className={classes.listContainer}>
      <ul className={classes.cell_wrap}>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:filters.COUNTRY')}
          </Text>
          <div className={classes.country}>
            {info.country !== '-' && info.country !== 'NA' ? (
              <Location country={info.country} type="c" withTooltip />
            ) : (
              <Text>{info.country}</Text>
            )}
          </div>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:filters.INDUSTRY')}
          </Text>
          <Text>{info.industry}</Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:about.WEBSITE')}
          </Text>
          <Text>
            {info.site !== '-' && info.site !== 'NA' ? (
              <a
                href={
                  info.site.indexOf('https://') !== -1
                    ? info.site
                    : `http://${info.site}`
                }
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('activity:about.GO_TO_SITE')}
              </a>
            ) : (
              info.site
            )}
          </Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:about.COMP_SIZE')}
          </Text>
          <Text>{sizeToCompany(info.size)}</Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:filters.PRICING_PLAN')}
          </Text>
          <Text>{info.plan}</Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.header}>
            {t('activity:filters.TYPE')}
          </Text>
          <Text>{info.type}</Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.header}>
            {t('activity:about.PARENT')}
          </Text>
          {info.partnerLinkable ? (
            <NavLink
              to={generateRegionalPath(
                `/customers/all-tenants/${info.partnerId}/general`
              )}
            >
              <Text className={classes.linkPartner}>{info.partnerId}</Text>
            </NavLink>
          ) : (
            <Text>{info.partnerId}</Text>
          )}
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.header}>
            {t('activity:about.ORIGIN')}
          </Text>
          <Text>{info.origin}</Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:filters.ACC_MANAGER')}
          </Text>
          <Text>{info.accountManager ? info.accountManager : 'NA'}</Text>
        </li>
        <li className={classes.cell}>
          <Text weight="m" className={classes.title}>
            {t('activity:about.CS_ENGINEER')}
          </Text>
          <Text>
            {info.customerSuccessEngineer ? info.customerSuccessEngineer : 'NA'}
          </Text>
        </li>
      </ul>
      {hasAssociationAccess && (
        <Button
          startIconUrl={`${links.imagesBaseUrl}/icons2/icon_checkPoint.svg`}
          onClick={
            info.isCheckpointTenant
              ? onClickTenantDetailsButton
              : onClickAssociateButton
          }
        >
          {info.isCheckpointTenant
            ? t('activity:tenantDetails.BUTTON')
            : t('activity:tenantsAssociation.ASSOCIATE')}
        </Button>
      )}
    </div>
  );
};

CompanyList.propTypes = {
  info: PropTypes.object.isRequired,
  companyName: PropTypes.string.isRequired,
  tenantId: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  requestHeaderData: PropTypes.func.isRequired,
  tenantUUID: PropTypes.string
};

export default CompanyList;
